/* eslint-disable jira/filename-case */
import React, { useEffect, type ReactNode } from 'react';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import { setCircuitBreakerOnIdle } from '@jira/platform__cookie-circuit-breaker/src/cookie-breaker-setter.tsx';
import { JSErrorBoundary } from '@jira/platform__js-error-boundary/src/JSErrorBoundary.tsx';
import SessionTracker from '@jira/platform__session-tracker/src/view/index.tsx';
import {
	addFeatureFlagAccessed,
	type FeatureFlagValue,
} from '@atlaskit/react-ufo/feature-flags-accessed';
import getUFORouteName from '@atlaskit/react-ufo/route-name';
import { collectSSRPlaceholderDimensions } from '@atlaskit/react-ufo/ssr-scripts/collectSSRPlaceholderDimensions';
import traceUFOPageLoad from '@atlaskit/react-ufo/trace-pageload';
import { useUFOTransitionCompleter } from '@atlaskit/react-ufo/trace-transition';
import { getVCObserver } from '@atlaskit/react-ufo/vc';
// eslint-disable-next-line jira/import/no-restricted-import
import { fg as platformFg } from '@atlaskit/platform-feature-flags/fg';
import { browserMetrics } from '@atlassian/browser-metrics/lib';
import { reportBootstrapError } from '@jira/platform__observability-bootstrap-sla-reporter/src/index.tsx';
import { initBrowserMetrics3 } from '@jira/platform__observability-browser-metrics';
import { CAPABILITY_HEADER_NAME } from '@jira/platform__observability-capabilities/src/constants.tsx';
import { setCapability } from '@jira/platform__observability-capabilities/src/services/active-capability-tracker/index.tsx';
import '@jira/platform__global-side-effects/src/index.tsx';
import { setMark } from '@atlassian/jira-common-performance/src/marks.tsx';
import { setSsrResourceTimeout } from '@atlassian/jira-common-performance/src/ssrResourceTimeout.tsx';
import { getTenantContext_DEPRECATED_DO_NOT_USE } from '@atlassian/jira-common-util-get-tenant-context/src/index.tsx';
import {
	EntryPointPlaceholderContext,
	getPlaceholdersFromHTML,
} from '@jira/platform__entry-points-point-placeholder/src/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { NonCritical } from '@jira/platform__services-non-critical/src/NonCriticalComponent.tsx';
import { Spa } from '@jira/platform__page-container-v2/src/ui/spa/index.tsx';
import { setMatchedRoute } from '@jira/platform__utils-router/src/common/utils/index.tsx';
import { useCurrentRoute } from '@jira/platform__utils-router/src/index.tsx';
import { StrictMode } from '@atlassian/jira-react-strict-mode/src/StrictMode.tsx';
import { JiraRouterLinkConfiguration } from '@jira/platform__routing-router-components/src/ui/router-link-config/index.tsx';
import { SpaRouter } from '@jira/platform__spa/src/view/spa-router/SpaRouter.tsx';
import {
	measureInitialPageLoadTiming,
	setInitialPageLoadTimingFromPerformanceMarks,
	stopInitialPageLoadTimingFromPerformanceMarkStart,
} from '@atlassian/jira-spa-performance-breakdown/src/utils/performance-marks-tools/index.tsx';
import { addInitialPageLoadTimings } from '@atlassian/jira-spa-performance-breakdown/src/utils/timings-from-eval-timings/index.tsx';
import { setPrefetchStartMark } from '@jira/platform__observability-spa-transition-resources-tracker/src/utils/prefetch-timing/index.tsx';
import createSpaHistory from '@jira/platform__spa/src/services/create-spa-history/index.tsx';
import { getSpaRouterContext } from '@jira/platform__spa/src/services/spa-router-context/index.tsx';
import initReactUFO from '@jira/platform__app-framework-ufo-interaction-metrics-init/src/index.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { getClientReceiver } from '@atlassian/server-to-client-comms/client-receiver';
import { LooselyLazy } from '@atlassian/react-loosely-lazy/init';
import { MODE } from '@atlassian/react-loosely-lazy';
import {
	type Route,
	matchRoute,
	useRouterActions,
	type RouterContext,
} from '@atlassian/react-resource-router';
import { AtlaspackMetricsConnector } from '@jira/platform__app-framework-atlaspack-global-metrics/src/index.tsx';
import {
	useRouterStoreActions,
	createRouterSelector,
} from '@atlassian/react-resource-router/src/controllers/router-store';
import { GlobalPageLoadExperience } from '@atlassian/ufo/ufo-experimental';
import { initEntryPointConfig } from '@jira/platform__entry-points-point-config/src/initEntryPointConfig.tsx';
import { preloadComponents } from '@jira/platform__layouts-route-layout/src/GlobalComponent.tsx';
import {
	sharedLayout,
	getSharedLayout,
} from '@jira/platform__layouts-jira-shared-layout/src/sharedLayout.tsx';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import getMeta from '@jira/platform__utils-get-meta/src/utils/get-meta/index.tsx';
import global from './global-core.tsx';
import { reportUnreportedStatsigInitialisationError } from './bootstrap/report-unreported-statsig-initialisation-error.tsx';
import {
	renderReactApp,
	getIsSsrHydrationEnabled,
	getIsServerStreamingEnabled,
} from './render-react-app.tsx';
import {
	initPlatformFeatureFlagsWithMetrics,
	verifyPlatformFeatureFlags,
} from './init-platform-feature-flags.tsx';

// Set SPA global
// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
window.__SPA__ = true;

LooselyLazy.init({
	mode: getIsSsrHydrationEnabled() ? MODE.HYDRATE : MODE.RENDER,
	crossOrigin: 'anonymous',
	// hide fg access from eslint, we can do it on "pure client
	react18: true,
});

const getMessages = () => {
	setMark('jira-spa/language-pack.start');
	const messages = require('language-pack');

	setMark('jira-spa/language-pack.end');
	return messages;
};

const superbatchInit = () => {
	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	window.DeferScripts = { deferState: 'done' };

	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	if (document.body?.classList.contains('deferred')) {
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		document.body?.classList.remove('deferred');
	}
};

const globalSettingsArgs = { navigation: false, upflow: false } as const;

const InstallGlobalComponentsOnce = () => {
	useEffect(() => {
		global.installLowPriorityApps(
			// @ts-expect-error - TS2345 - Argument of type 'Partial<{ readonly navigation: false; readonly upflow: false; }>' is not assignable to parameter of type 'LowPriorityAppsArgs'.
			omit(globalSettingsArgs, ['flags', 'navigation', 'renderNavigation']),
		);

		setMark('JIRA_SPA_ENTRY_GLOBAL_INSTALL');
	}, []);
	return null;
};

// this will move low priority apps to after TTAI (currently just the async-profilecard (legacy profilecard, only used on legacy experiences like GIN and legacy GIC))
const LoadPhaseSwitcher = () => {
	return (
		<NonCritical id="spa-main.client-router.non-critical" teamName="delorean-spa">
			<InstallGlobalComponentsOnce />
		</NonCritical>
	);
};

const updateCapability = (capability: string | null) => {
	setCapability(capability);

	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	if (capability === null && window.AJS?.$?.ajaxSettings?.headers?.[CAPABILITY_HEADER_NAME]) {
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		delete window.AJS.$.ajaxSettings.headers[CAPABILITY_HEADER_NAME];
	} else {
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		window.AJS?.$?.ajaxSetup({ headers: { [CAPABILITY_HEADER_NAME]: capability } });
	}
};

// @ts-expect-error - TS7031 - Binding element 'routes' implicitly has an 'any' type.
const CapabilityTrackerConnector = ({ routes }) => {
	const { registerBlock } = useRouterActions();
	registerBlock((nextLocation: Location) => {
		const nextMatchObject = matchRoute(routes, nextLocation.pathname, nextLocation.search, '');
		updateCapability(nextMatchObject?.route.meta?.capability || null);
		return true;
	});
	const route = useCurrentRoute();
	updateCapability(route.meta?.capability || null);
	return null;
};

type FeatureGateMismatch = {
	expected: boolean;
	fgResult: boolean;
	platformFgResult: boolean;
};

// This verification is called after render to ensure that nothing is overriding the resolved value
const verifyAlwaysOffFeatureGates = () => {
	// eslint-disable-next-line jira/ff/inline-usage
	const fgResult = fg('jfp-magma-always-off-fg-test');
	const platformFgResult = platformFg('jfp-magma-always-off-fg-test');

	if (fgResult !== false || platformFgResult !== false) {
		return [
			{
				expected: false,
				fgResult,
				platformFgResult,
			},
		];
	}

	return [];
};

const reportFeatureGateMismatches = () => {
	// This one is populated on SSR so we can check if Statsig is working correctly
	const ssrMismatches: FeatureGateMismatch[] =
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		window.SPA_STATE?.featureGateMismatch?.mismatches ?? [];

	const postRenderMismatches = verifyAlwaysOffFeatureGates();

	const allMismatches = [...ssrMismatches, ...postRenderMismatches];

	if (allMismatches.length) {
		log.safeErrorWithoutCustomerData(
			'feature-gate-verification',
			'Statsig feature gate mismatch detected',
			{ mismatches: JSON.stringify(allMismatches) },
		);
	}
};

/**
 * Records the SSR execution-timeout experiment exposure for streaming-eligible route loads. Static
 * fallback boots re-derive the route's streaming eligibility here (the same check the server runs),
 * because SPA_STATE.serverIsStreaming is not present on those loads.
 */
const callExpValForSsrStreamingExposures = (
	route: { isStreamingEnabled?: boolean } | undefined,
): void => {
	if (!fg('jfp-magma-ssr-streaming-monitoring-fixes')) {
		return;
	}
	if (route?.isStreamingEnabled ?? fg('jfp-magma-ssr-streaming-enable-rest')) {
		expVal('jfp-magma-ssr-execution-timeout-exp', 'timeout', 2600);
	}
};

const main = async (
	getInitialRoutes: () => Route[],
	loadLazyRoutes?: () => Promise<() => Route[]>,
) => {
	if (!fg('jfp-magma-data-ssr-placeholder-retain')) {
		collectSSRPlaceholderDimensions(document, window, true);
	}

	initReactUFO();
	initBrowserMetrics3();
	initPlatformFeatureFlagsWithMetrics();
	verifyPlatformFeatureFlags();
	initEntryPointConfig();
	getClientReceiver();
	addInitialPageLoadTimings();
	stopInitialPageLoadTimingFromPerformanceMarkStart('jira-spa/init', 'jira-spa.js:eval-start');
	setMark('jira-spa/setup.start');

	const routes = measureInitialPageLoadTiming('jira-spa/setup/routes', () => getInitialRoutes());
	const history = measureInitialPageLoadTiming('jira-spa/setup/history', () =>
		createSpaHistory(routes),
	);

	const initRoute = measureInitialPageLoadTiming('jira-spa/setup/match-route', () => {
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		const matchedRoute = matchRoute(routes, window.location.pathname, window.location.search);
		setMatchedRoute(matchedRoute?.route, matchedRoute?.match);
		return matchedRoute;
	});

	if (fg('nin_ssr_resource_timeout_info')) {
		setSsrResourceTimeout(initRoute?.route?.bifrost?.fetchTimeout);
	}

	if (initRoute != null) {
		const ufoName = getUFORouteName(initRoute.route);
		traceUFOPageLoad(ufoName, initRoute.route.name);
	}

	const BrowserMetricsConnector = () => {
		const route = useCurrentRoute();
		browserMetrics.setRoute(route.name);

		useUFOTransitionCompleter();

		GlobalPageLoadExperience.setPageLoadId(route.perfMetricKey || 'UNKNOWN');
		return null;
	};

	GlobalPageLoadExperience.startPageLoad('UNKNOWN', true);

	const messages = getMessages();
	const tenantContext = getTenantContext_DEPRECATED_DO_NOT_USE();
	const { locale } = tenantContext;
	const routerProps = {
		history,
		routes,
		basePath: '',
		initialRoute: initRoute?.route,
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		resourceData: window.SPA_STATE,
		resourceContext: getSpaRouterContext({
			tenantContext,
		}),
		onPrefetch: ({ route, match }: RouterContext) => {
			setPrefetchStartMark(match);
			if (!route.forPaint) {
				return;
			}
			route.forPaint.map((component) => component.preload && component.preload());
		},
		isLazyRoutes: typeof loadLazyRoutes === 'function',
	};

	const SpaClientRouterTemplate = ({
		children,
		trackedRoutes,
	}: {
		children: ReactNode;
		trackedRoutes: typeof routes;
	}) => (
		<SpaRouter
			routerProps={routerProps}
			before={
				<JSErrorBoundary
					id="spa-main.client-router"
					packageName="jiraEntry"
					fallback="unmount"
					teamName="delorean-spa"
				>
					<AtlaspackMetricsConnector />
					<BrowserMetricsConnector />
					{!expVal('jpo-42550-improve-capability-tracking', 'isEnabled', false) && (
						<CapabilityTrackerConnector routes={trackedRoutes} />
					)}
					{/* @ts-expect-error - TS2739 - Type '{}' is missing the following properties from type 'RouteContext': location, query, route, match, action */}
					<SessionTracker />
				</JSErrorBoundary>
			}
			after={<LoadPhaseSwitcher />}
		>
			{children}
		</SpaRouter>
	);

	let SpaClientRouter;
	if (typeof loadLazyRoutes === 'function') {
		const useRouterRoutes = createRouterSelector((state) => state.routes);
		SpaClientRouter = ({ children }: { children: ReactNode }) => {
			const actions = useRouterStoreActions();
			const currentRoutes = useRouterRoutes();
			useEffect(() => {
				let isCancelled = false;
				const loadRoutes = async () => {
					if (!isCancelled) {
						const getLazyRoutes = await loadLazyRoutes();
						await actions.setRoutes(getLazyRoutes());
					}
				};
				loadRoutes();
				return () => {
					isCancelled = true;
				};
			}, [actions]);

			return (
				<SpaClientRouterTemplate trackedRoutes={currentRoutes}>{children}</SpaClientRouterTemplate>
			);
		};
	} else {
		SpaClientRouter = ({ children }: { children: ReactNode }) => (
			<SpaClientRouterTemplate trackedRoutes={routes}>{children}</SpaClientRouterTemplate>
		);
	}
	const AppWithoutPlaceholders = ({
		entryPointPlaceholders,
	}: {
		entryPointPlaceholders?: Map<string, string>;
	}) => {
		const spaContent = (
			<Spa
				locale={locale}
				messages={messages}
				Router={SpaClientRouter}
				initRoute={initRoute}
				routes={routes}
				routerLinkComponent={JiraRouterLinkConfiguration}
			/>
		);

		return (
			<StrictMode>
				<UFOSegment name="jira-spa">
					{entryPointPlaceholders ? (
						<EntryPointPlaceholderContext.Provider value={entryPointPlaceholders}>
							{spaContent}
						</EntryPointPlaceholderContext.Provider>
					) : (
						spaContent
					)}
				</UFOSegment>
				<>{/* placeholder for SSR:RenderTracerMark */}</>
			</StrictMode>
		);
	};

	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	window.__NAV_VERSION__ = '3.0.0';

	stopInitialPageLoadTimingFromPerformanceMarkStart('jira-spa/setup', 'jira-spa/setup.start', true);

	setMark('_jira-spa/spa-shell.start');

	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	const container = document.getElementById('jira-frontend');

	if (container) {
		getVCObserver().setSSRElement(container);
		getVCObserver().setReactRootRenderStart();
		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				getVCObserver().setReactRootRenderStop();
			});
		});
		const isSsrHydrationEnabled = getIsSsrHydrationEnabled();
		const isServerStreamingEnabled = getIsServerStreamingEnabled();
		const entryPointPlaceholders = isSsrHydrationEnabled ? undefined : getPlaceholdersFromHTML();

		callExpValForSsrStreamingExposures(initRoute?.route);

		renderReactApp(<AppWithoutPlaceholders entryPointPlaceholders={entryPointPlaceholders} />, {
			isSsrHydrationEnabled,
			isServerStreamingEnabled,
			container,
		});

		// Replay SSR-evaluated feature gates into UFO so SSR-evaluated gates show up on UFO
		// interactions on the client. The global is set during SSR in jira/src/packages/ssr/frontend-server/src/orchestrateResponse.tsx
		const ssrFeatureFlagsAccessed: Record<string, unknown> | undefined =
			// @ts-expect-error - __SSR_FEATURE_FLAGS_ACCESSED__ is not declared on Window (globals.d.ts is ratcheted)
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			window.__SSR_FEATURE_FLAGS_ACCESSED__;
		if (ssrFeatureFlagsAccessed && typeof ssrFeatureFlagsAccessed === 'object') {
			Object.keys(ssrFeatureFlagsAccessed).forEach((key) => {
				addFeatureFlagAccessed(
					key,
					// oxlint-disable-next-line typescript/consistent-type-assertions
					ssrFeatureFlagsAccessed[key] as FeatureFlagValue,
				);
			});
		}
	}

	stopInitialPageLoadTimingFromPerformanceMarkStart(
		'jira-spa/spa-shell',
		'_jira-spa/spa-shell.start',
	);
	setMark('JIRA_SPA_ENTRY_REACT_DOM_RENDER');
	setTimeout(() => {
		setInitialPageLoadTimingFromPerformanceMarks(
			'jira-spa',
			'commons-entry.js:eval-stop',
			'jira-spa.js:eval-stop',
		);
	});

	if (expVal('uip_preprod_slow', 'value', 0) > 0) {
		setMark('slow-start');
		const end = performance.now() + expVal('uip_preprod_slow', 'value', 0);
		while (performance.now() < end) {
			// do nothing
		}
		setMark('slow-stop');
	}

	global.installHighPriorityApps(
		// @ts-expect-error - TS2345 - Argument of type 'Partial<{ readonly navigation: false; readonly upflow: false; }>' is not assignable to parameter of type 'HighPriorityAppsArgs'.
		pick(globalSettingsArgs, ['flags', 'navigation', 'renderNavigation']),
	);

	if (initRoute?.route?.bifrost?.circuitBreakerConfig) {
		const userId = getMeta('ajs-atlassian-account-id') ?? undefined;
		const cloudId = getMeta('ajs-cloud-id') ?? undefined;
		setCircuitBreakerOnIdle({
			...initRoute.route.bifrost.circuitBreakerConfig,
			userId,
			cloudId,
		});
	}

	superbatchInit();
	if (!__SERVER__) {
		if (fg('jpx-870-stable-ui-for-criterion')) {
			preloadComponents(getSharedLayout());
		} else {
			preloadComponents(sharedLayout);
		}
	}

	return null;
};

export const startJiraSpa = async (
	getInitialRoutes: () => Route[],
	loadLazyRoutes?: () => Promise<() => Route[]>,
): Promise<null> => {
	// In React 18: 500ms is the default value internally in react-dom
	// In React 19: 300ms is the default value
	// In React 19.2: This value becomes dynamic https://react.dev/blog/2025/10/01/react-19-2
	//
	// Based of our experiment (`jfp-magma-initial-react-fallback-throttle-ms`), 100ms appears to be the optimal value for Jira *at this point in time*
	// It can and should be removed or tweaked when we upgrade React
	globalThis.REACT_FALLBACK_THROTTLE_MS = 100;

	return main(getInitialRoutes, loadLazyRoutes)
		.catch((reason) => reportBootstrapError(reason))
		.finally(() => {
			reportUnreportedStatsigInitialisationError();
			reportFeatureGateMismatches();
		});
};
